'use client'

import * as React from 'react'
import { accentChain, createShader } from 'glimm'

/* Theme-flip sweep — an iridescent foil curtain wipes across the viewport and
   the theme swaps underneath it while it is fully opaque.

   We drive glimm's shader controller directly rather than using its own
   `playSweep`, which has three properties that read as unpolished here:

     · it sets alpha to peak on frame 0 (a step, not a ramp), so the screen
       visibly pops the instant you click;
     · its midpoint callback is fire-and-forget, so the band races on to
       progress 1 and then sits frozen off-screen while the outro fades
       something already near-invisible — the sweep looks like it dies early;
     · at its default band width the un-swept side of the screen is only ~30%
       covered when the theme flips, so the flip is plainly visible.

   Instead: one clock, alpha ramps in and out around a travel curve, and the
   flip happens at the exact centre where coverage is total.

   Coverage maths (from the shader): a pixel's alpha is
     intensity · peakAlpha · entryFade,  entryFade = 0.2 + 3.2·p·(1-p)
   with `intensity` driven by band = exp(-d²·bandTight). peakAlpha is allowed
   up to 1.5 and the output is clamped, so anything above 1 acts as a coverage
   gain that saturates the mid-tones to fully opaque. BAND_TIGHT/PEAK_ALPHA
   below are chosen so the *worst-lit* pixel on screen still reaches alpha 1
   across the whole flip window — see the note on each constant. */

const PALETTE = accentChain(['#37ecb0', '#2dd4bf', '#7dd3fc', '#e4e4e7'])

/** Lower = wider band. 0.85 puts the 50%-alpha half-width near a full
 *  viewport, which is what makes total coverage possible at all. */
const BAND_TIGHT = 0.85
/** >1 is a coverage gain, not brightness — the shader clamps final alpha. */
const PEAK_ALPHA = 1.45
/** Calmer crest than the 0.55 default; a curtain this wide doesn't need a
 *  hard specular ridge running through it. */
const SWELL = 0.4

/** Percent the curtain extends beyond each viewport edge — see the note where
 *  the canvas is styled. Applied on both axes so it holds for any direction. */
const OVERHANG = 5

const TOTAL_MS = 1250
const INTRO_MS = 170
const OUTRO_MS = 380
/** Band centre crosses the screen centre at progress 0.5 — peak coverage. */
const FLIP_AT = 0.5

/** Fired around a sweep so expensive decorative canvases can idle. Their work
 *  is invisible under the curtain, and competing for the main thread is what
 *  makes the band judder. */
export const SWEEP_START_EVENT = 'themesweep:start'
export const SWEEP_END_EVENT = 'themesweep:end'

const easeInOutCubic = (p: number) =>
  p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2
const easeOutCubic = (p: number) => 1 - Math.pow(1 - p, 3)
const clamp01 = (p: number) => (p < 0 ? 0 : p > 1 ? 1 : p)

type Controller = ReturnType<typeof createShader>

type Ctx = { sweepTheme: (flip: () => void) => void }

const ThemeSweepContext = React.createContext<Ctx | null>(null)

export function useThemeSweep(): Ctx {
  const ctx = React.useContext(ThemeSweepContext)
  // The toggle renders in the header on every route, so a missing provider
  // should degrade to a plain flip rather than throw.
  return ctx ?? { sweepTheme: (flip) => flip() }
}

export function ThemeSweepProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const hostRef = React.useRef<HTMLDivElement>(null)
  const ctrlRef = React.useRef<Controller>(null)
  const rafRef = React.useRef(0)
  const idleRef = React.useRef(0)

  /* Creating the GL context and compiling the shader costs ~90ms even on a
     real GPU. Paid inside the click handler it freezes the first frame of the
     sweep outright, so it is paid up front while the browser is idle and the
     context is then kept warm. It is released whenever the tab is hidden so an
     unattended page isn't holding a live GL loop. */
  React.useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let idleId = 0
    const schedulePrewarm = () => {
      const ric = window.requestIdleCallback
      if (ric) idleId = ric(() => ensureController(), { timeout: 3000 })
      else idleId = window.setTimeout(() => ensureController(), 1200)
    }
    const cancelPrewarm = () => {
      if (!idleId) return
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId)
      else window.clearTimeout(idleId)
      idleId = 0
    }

    const onVisibility = () => {
      if (document.hidden) {
        cancelPrewarm()
        ctrlRef.current?.destroy()
        ctrlRef.current = null
      } else {
        schedulePrewarm()
      }
    }

    schedulePrewarm()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      cancelPrewarm()
      document.removeEventListener('visibilitychange', onVisibility)
      cancelAnimationFrame(rafRef.current)
      window.clearTimeout(idleRef.current)
      ctrlRef.current?.destroy()
      ctrlRef.current = null
    }
    // ensureController is stable (useCallback with no deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ensureController = React.useCallback((): Controller | null => {
    if (ctrlRef.current) return ctrlRef.current
    const host = hostRef.current
    if (!host) return null

    const canvas = document.createElement('canvas')
    canvas.setAttribute('aria-hidden', 'true')
    /* The shader fades the curtain out across the first and last 1.5% of the
       axis perpendicular to travel (`vfade`), which would leave a sliver of
       un-covered page along the top and bottom edges. Overhanging the canvas
       past the viewport puts those faded edges off-screen; OVERHANG must
       exceed 1.5% of the resulting canvas size, so anything over ~1.6% works. */
    host.appendChild(canvas)
    Object.assign(canvas.style, {
      position: 'absolute',
      top: `-${OVERHANG}%`,
      left: `-${OVERHANG}%`,
      width: `${100 + OVERHANG * 2}%`,
      height: `${100 + OVERHANG * 2}%`,
      display: 'block',
      pointerEvents: 'none',
    })

    const ctrl = createShader({
      canvas,
      palette: PALETTE,
      bandTight: BAND_TIGHT,
      direction: 'ltr',
    })
    if (!ctrl) {
      canvas.remove()
      return null
    }
    ctrl.setSwellAmount(SWELL)
    ctrl.setAlpha(0)
    ctrl.setProgress(0)
    ctrlRef.current = ctrl
    return ctrl
  }, [])

  const sweepTheme = React.useCallback(
    (flip: () => void) => {
      const reduced = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
      const ctrl = reduced ? null : ensureController()
      if (!ctrl) {
        flip()
        return
      }

      cancelAnimationFrame(rafRef.current)
      window.clearTimeout(idleRef.current)

      ctrl.setAlpha(0)
      ctrl.setProgress(0)
      window.dispatchEvent(new Event(SWEEP_START_EVENT))

      let flipped = false
      let start = 0

      const tick = (ts: number) => {
        // Anchor to the first painted frame, not to click time, so the GL
        // compile on a cold context can't eat the head of the animation.
        if (!start) start = ts
        const t = ts - start
        const u = clamp01(t / TOTAL_MS)
        const progress = easeInOutCubic(u)

        const intro = easeOutCubic(clamp01(t / INTRO_MS))
        const outro = easeOutCubic(clamp01((TOTAL_MS - t) / OUTRO_MS))
        ctrl.setProgress(progress)
        ctrl.setAlpha(PEAK_ALPHA * Math.min(intro, outro))

        if (!flipped && progress >= FLIP_AT) {
          flipped = true
          flip()
        }

        if (u < 1) {
          rafRef.current = requestAnimationFrame(tick)
        } else {
          ctrl.setAlpha(0)
          ctrl.setProgress(0)
          window.dispatchEvent(new Event(SWEEP_END_EVENT))
        }
      }

      rafRef.current = requestAnimationFrame(tick)
    },
    [ensureController]
  )

  const value = React.useMemo(() => ({ sweepTheme }), [sweepTheme])

  return (
    <ThemeSweepContext.Provider value={value}>
      {children}
      <div
        ref={hostRef}
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 9999,
        }}
      />
    </ThemeSweepContext.Provider>
  )
}
